/**
 * History: the unified, day-by-day log of everything the family tracked.
 * Built to answer the pediatrician's questions at the 3-5 day visit:
 * how many feeds, how many wet and dirty diapers, and the weight trend.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useNav } from '../App';
import FeedSheet from '../components/FeedSheet';
import { DiaperSheet, PumpSheet, SleepSheet, WeightSheet } from '../components/TrackSheets';
import { Icons, radioGroupNav, radioTabIndex, ScreenHeader } from '../components/ui';
import { useLocale, useT, type Dict } from '../i18n/i18n';
import { useApp } from '../lib/store';
import {
  addDays,
  dayOfLife,
  formatDate,
  formatTime,
  localDateOf,
  todayLocalDate,
} from '../lib/time';
import { gramsToDisplay, mlToDisplay, weightChangePct } from '../lib/units';
import type { EventType, FeedEvent, TrackedEvent } from '../lib/types';

type Filter = 'all' | EventType;

const FILTERS: Filter[] = ['all', 'feed', 'diaper', 'pump', 'sleep', 'weight'];
const INITIAL_DAYS = 14;
const SEP = ' · ';

// ── Day grouping ──────────────────────────────────────────────────────────

interface DaySummary {
  feeds: number;
  wet: number;
  dirty: number;
  pumpMl: number;
  sleepSeconds: number;
}

interface DayGroup {
  date: string;
  events: TrackedEvent[];
  summary: DaySummary;
}

function summarize(events: TrackedEvent[]): DaySummary {
  const s: DaySummary = { feeds: 0, wet: 0, dirty: 0, pumpMl: 0, sleepSeconds: 0 };
  for (const e of events) {
    if (e.type === 'feed') {
      s.feeds += 1;
    } else if (e.type === 'diaper') {
      if (e.wet) s.wet += 1;
      if (e.dirty) s.dirty += 1;
    } else if (e.type === 'pump') {
      s.pumpMl += (e.leftMl ?? 0) + (e.rightMl ?? 0);
    } else if (e.type === 'sleep' && e.endAt) {
      const sec = (new Date(e.endAt).getTime() - new Date(e.at).getTime()) / 1000;
      if (sec > 0) s.sleepSeconds += sec;
    }
  }
  return s;
}

function durationLabel(t: Dict, seconds: number): string {
  const totalMin = Math.round(seconds / 60);
  const h = Math.floor(totalMin / 60);
  let m = totalMin % 60;
  if (h === 0 && m === 0 && seconds > 0) m = 1;
  return t.common.time.durationHM(h, m);
}

// ── Icons (decorative; match the stroke style in components/ui.tsx) ───────

function GlyphIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function eventIcon(type: EventType): ReactNode {
  switch (type) {
    case 'feed':
      return (
        <GlyphIcon>
          <path d="M12 2v2" />
          <path d="M9.5 8V6.5A2.5 2.5 0 0 1 12 4a2.5 2.5 0 0 1 2.5 2.5V8" />
          <rect x="8" y="8" width="8" height="13" rx="2.5" />
          <path d="M8 12.5h8M8 16.5h8" />
        </GlyphIcon>
      );
    case 'diaper':
      return (
        <GlyphIcon>
          <path d="M12 3.5s6 6.2 6 10a6 6 0 0 1-12 0c0-3.8 6-10 6-10Z" />
        </GlyphIcon>
      );
    case 'weight':
      return (
        <GlyphIcon>
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <path d="M8.5 10.5a3.5 3.5 0 0 1 7 0" />
          <path d="m12 10.5 1.7-1.9" />
        </GlyphIcon>
      );
    case 'pump':
      return (
        <GlyphIcon>
          <path d="M8 3h7l-1.5 5.5h-4L8 3Z" />
          <path d="M11.5 8.5V12" />
          <rect x="9" y="12" width="5" height="9" rx="1.5" />
        </GlyphIcon>
      );
    case 'sleep':
      return (
        <GlyphIcon>
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
        </GlyphIcon>
      );
  }
}

function SummaryChip({ emoji, label }: { emoji: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-surface-2 px-2.5 py-1 text-xs font-bold text-ink-soft">
      <span aria-hidden="true">{emoji}</span>
      {label}
    </span>
  );
}

// ── Screen ────────────────────────────────────────────────────────────────

export default function HistoryScreen() {
  const t = useT();
  const locale = useLocale();
  const nav = useNav();
  const { events, profile, settings, device } = useApp();

  const [filter, setFilter] = useState<Filter>('all');
  const [visibleDays, setVisibleDays] = useState(INITIAL_DAYS);
  const [editing, setEditing] = useState<TrackedEvent | null>(null);

  // events from the store are already non-deleted and newest first, so Map
  // insertion order gives us newest day first with newest events on top.
  const groups = useMemo<DayGroup[]>(() => {
    const filtered = filter === 'all' ? events : events.filter((e) => e.type === filter);
    const byDay = new Map<string, TrackedEvent[]>();
    for (const e of filtered) {
      const date = localDateOf(e.at);
      const list = byDay.get(date);
      if (list) list.push(e);
      else byDay.set(date, [e]);
    }
    return [...byDay.entries()].map(([date, dayEvents]) => ({
      date,
      events: dayEvents,
      summary: summarize(dayEvents),
    }));
  }, [events, filter]);

  const today = todayLocalDate();
  const yesterday = addDays(today, -1);

  const dayLabel = (date: string): string => {
    if (date === today) return t.common.time.today;
    if (date === yesterday) return t.common.time.yesterday;
    return formatDate(date, locale);
  };

  const dayOfLifeFor = (date: string): number | null => {
    if (!profile?.birthAt) return null;
    const [y, m, d] = date.split('-').map(Number);
    const n = dayOfLife(profile.birthAt, new Date(y, m - 1, d));
    return n !== null && n >= 1 ? n : null;
  };

  const feedDetail = (e: FeedEvent): string => {
    if (e.method === 'bottle') {
      const parts: string[] = [];
      if (e.amountMl != null) parts.push(mlToDisplay(e.amountMl, settings.volumeUnit));
      if (e.contents) parts.push(t.common.contents[e.contents]);
      return parts.length > 0 ? parts.join(SEP) : t.history.row.bottle;
    }
    const parts: string[] = [];
    if (e.leftSeconds) {
      parts.push(t.history.row.sideDuration(t.common.sides.left, durationLabel(t, e.leftSeconds)));
    }
    if (e.rightSeconds) {
      parts.push(
        t.history.row.sideDuration(t.common.sides.right, durationLabel(t, e.rightSeconds)),
      );
    }
    if (parts.length === 0 && e.side) parts.push(t.common.sides[e.side]);
    return parts.length > 0 ? parts.join(SEP) : t.history.row.breast;
  };

  const detailFor = (e: TrackedEvent): string => {
    switch (e.type) {
      case 'feed':
        return feedDetail(e);
      case 'diaper':
        if (e.wet && e.dirty) return t.history.row.diaperBoth;
        if (e.dirty) return t.history.row.diaperDirty;
        if (e.wet) return t.history.row.diaperWet;
        return '';
      case 'weight': {
        const parts = [gramsToDisplay(e.grams, settings.weightUnit)];
        const birthG = profile?.birthWeightG;
        if (birthG && birthG > 0) {
          const pct = weightChangePct(e.grams, birthG);
          const rounded = Math.round(Math.abs(pct) * 10) / 10;
          if (rounded === 0) parts.push(t.history.row.atBirthWeight);
          else if (pct < 0) parts.push(t.history.row.belowBirthWeight(rounded));
          else parts.push(t.history.row.aboveBirthWeight(rounded));
        }
        return parts.join(SEP);
      }
      case 'pump': {
        const total = (e.leftMl ?? 0) + (e.rightMl ?? 0);
        const parts: string[] = [];
        if (total > 0) parts.push(mlToDisplay(total, settings.volumeUnit));
        if (e.durationSeconds) parts.push(durationLabel(t, e.durationSeconds));
        return parts.join(SEP);
      }
      case 'sleep': {
        if (!e.endAt) return t.history.row.sleepOngoing;
        const sec = Math.max(0, (new Date(e.endAt).getTime() - new Date(e.at).getTime()) / 1000);
        return durationLabel(t, sec);
      }
    }
  };

  const primaryFor = (e: TrackedEvent): string => {
    const type = t.common.eventTypes[e.type];
    const detail = detailFor(e);
    return detail ? `${type}${SEP}${detail}` : type;
  };

  const closeSheet = () => setEditing(null);

  const visibleGroups = groups.slice(0, visibleDays);
  const nothingLogged = events.length === 0;

  return (
    <>
      <ScreenHeader
        title={t.history.title}
        subtitle={t.history.subtitle}
        onSettings={() => nav.go('settings')}
      />

      <div className="px-4 pb-4">
        {/* Filter chips */}
        <div
          role="radiogroup"
          aria-label={t.history.filterLabel}
          onKeyDown={(e) => radioGroupNav(e, (i) => setFilter(FILTERS[i]))}
          className="-mx-4 flex gap-2 overflow-x-auto px-4 py-3"
        >
          {FILTERS.map((f, i) => {
            const active = filter === f;
            return (
              <button
                key={f}
                type="button"
                role="radio"
                aria-checked={active}
                tabIndex={radioTabIndex(active, i, true)}
                onClick={() => setFilter(f)}
                className={`min-h-11 shrink-0 rounded-full border px-4 text-sm font-bold transition-colors ${
                  active
                    ? 'border-primary bg-primary text-on-primary'
                    : 'border-line bg-surface text-ink-soft'
                }`}
              >
                {f === 'all' ? t.history.filterAll : t.common.eventTypes[f]}
              </button>
            );
          })}
        </div>

        {nothingLogged ? (
          <div className="card mt-3 flex flex-col items-center gap-3 px-6 py-10 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-soft text-primary">
              {Icons.history('h-7 w-7')}
            </span>
            <h2 className="text-lg">{t.history.empty.title}</h2>
            <p className="text-sm text-ink-soft">{t.history.empty.body}</p>
            <button
              type="button"
              onClick={() => nav.go('track')}
              className="btn-primary mt-1 min-h-11 px-6 py-3"
            >
              {t.history.empty.cta}
            </button>
          </div>
        ) : groups.length === 0 ? (
          <p className="mt-8 px-2 text-center text-ink-soft">{t.history.empty.noMatches}</p>
        ) : (
          <>
            {visibleGroups.map((group) => {
              const dol = dayOfLifeFor(group.date);
              const s = group.summary;
              const hasChips =
                s.feeds > 0 || s.wet > 0 || s.dirty > 0 || s.pumpMl > 0 || s.sleepSeconds > 0;
              return (
                <section key={group.date} className="mt-4">
                  <div className="flex flex-wrap items-baseline gap-x-2 px-1">
                    <h2 className="text-lg">{dayLabel(group.date)}</h2>
                    {dol !== null && (
                      <span className="text-sm font-bold text-primary">
                        {t.common.time.dayOfLife(dol)}
                      </span>
                    )}
                  </div>
                  {hasChips && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5 px-1">
                      {s.feeds > 0 && (
                        <SummaryChip emoji="🍼" label={t.history.daySummary.feeds(s.feeds)} />
                      )}
                      {s.wet > 0 && (
                        <SummaryChip emoji="💧" label={t.history.daySummary.wet(s.wet)} />
                      )}
                      {s.dirty > 0 && (
                        <SummaryChip emoji="💩" label={t.history.daySummary.dirty(s.dirty)} />
                      )}
                      {s.pumpMl > 0 && (
                        <SummaryChip
                          emoji="🥛"
                          label={t.history.daySummary.pumped(
                            mlToDisplay(s.pumpMl, settings.volumeUnit),
                          )}
                        />
                      )}
                      {s.sleepSeconds > 0 && (
                        <SummaryChip
                          emoji="😴"
                          label={t.history.daySummary.slept(durationLabel(t, s.sleepSeconds))}
                        />
                      )}
                    </div>
                  )}
                  <div className="card mt-2 divide-y divide-line overflow-hidden">
                    {group.events.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => setEditing(e)}
                        className="flex min-h-[56px] w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-2 active:bg-surface-2"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                          {eventIcon(e.type)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-bold text-ink">
                            {primaryFor(e)}
                          </span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-ink-soft">
                            {formatTime(e.at, locale)}
                            {device && e.role !== device.role && (
                              <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-bold text-accent">
                                {e.role === 'partner'
                                  ? t.common.sync.loggedByPartner
                                  : t.common.sync.loggedByMom}
                              </span>
                            )}
                          </span>
                        </span>
                        {Icons.chevronRight('h-5 w-5 shrink-0 text-ink-faint')}
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}

            {groups.length > visibleDays && (
              <button
                type="button"
                onClick={() => setVisibleDays((v) => v + INITIAL_DAYS)}
                className="btn-secondary mx-auto mt-5 block min-h-11 px-6 py-3"
              >
                {t.history.showEarlier}
              </button>
            )}
          </>
        )}
      </div>

      {/* Edit sheets: one per event type, opened by tapping a row */}
      <FeedSheet
        open={editing?.type === 'feed'}
        onClose={closeSheet}
        editing={editing?.type === 'feed' ? editing : undefined}
      />
      <DiaperSheet
        open={editing?.type === 'diaper'}
        onClose={closeSheet}
        editing={editing?.type === 'diaper' ? editing : undefined}
      />
      <WeightSheet
        open={editing?.type === 'weight'}
        onClose={closeSheet}
        editing={editing?.type === 'weight' ? editing : undefined}
      />
      <PumpSheet
        open={editing?.type === 'pump'}
        onClose={closeSheet}
        editing={editing?.type === 'pump' ? editing : undefined}
      />
      <SleepSheet
        open={editing?.type === 'sleep'}
        onClose={closeSheet}
        editing={editing?.type === 'sleep' ? editing : undefined}
      />
    </>
  );
}
